import React, { useState, useEffect, useMemo } from "react";
import { Button, Input } from "reactstrap";
import { SelectPicker } from "rsuite";
import AppSpinner from "../../elements/AppSpinner";
import ModulePicker from "../Modules/ModulePicker";
import useModule from "../../hooks/useModule";
import SelectionPage from "../Drawers/CreateQuickCreateDrawer/SelectionPage";
import CreateFromGlobalPage from "../Drawers/CreateQuickCreateDrawer/CreateFromGlobalPage";
import StrUtils from "../../utils/StrUtils";
import CustomToggle from "../Drawers/Fields/CustomToggle";
import ModuleFieldsPicker from "./ModuleFieldsPicker";
import { DrawerProps } from "../Drawers/DrawerTypes";
import { Field, Module } from "../../types/TenantInterfaces";
import {
  CreateTableItem,
  UpdateTableItem,
} from "../PageView/PageViewTable/usePageViewTable";
import AppDrawer, { DrawerError } from "../Drawers/AppDrawer";
import RequiredTitle from "../../elements/RequiredTitle";
import { Layout } from "../../types/GlobalInterfaces";
import CardsLayoutEditor, { CardTypes } from "./CardsLayout/CardsLayoutEditor";
import useCalculateFieldGroups, {
  FieldGroup,
} from "../../api/api-hooks/modules/fields/useCalculateFieldGroups";
import { useParams } from "react-router-dom";
import SkeletonLoader from "../../elements/SkeletonLoader";
import { useToggle } from "@mantine/hooks";
import IconButton from "../../elements/IconButton";
import ManageItems from "../ManageItems/ManageItems";

const GroupByLayouts = ["TableLayouts", "BoardLayouts", "TimelineLayouts"];
const GroupByTypes = ["Board", "Timeline"];

export type LayoutTypes =
  | "TableLayouts"
  | "CardsLayouts"
  | "TimelineLayouts"
  | "BoardLayouts"
  | "CalendarConfig";

interface Props extends DrawerProps {
  currentModule: Module;
  handleCreate: (createItemPayload: CreateTableItem) => void;
  handleUpdate: ((updateItemPayload: UpdateTableItem) => void) | undefined;
  updateKey: LayoutTypes;
  title: string;
  submitting: boolean;
  existingLayout?: Layout;
  fieldSchema: Field | undefined;
  limit?: number;
  useModulePicker?: boolean;
  useTypeSelect?: boolean;
  usePageSelection?: boolean;
  existingLayouts?: any[];
  handleAddGlobals?: any;
  layoutT?: "Table" | "Cards" | "Board" | "Timeline" | "Calendar";
}

/**
 * CreateTableLayoutDrawer - Drawer used for creating / editing layouts.
 * Made for TableLayouts, CardsLayouts, and BoardLayouts - But open to be used more.
 *
 * @param {string} updateKey - Should be plural - "TableLayouts" | "CardsLayouts" | "BoardLayouts"
 * @param {number | null} limit - Allows you to limit the number of fields you can add to a layout. Used with "Cards" and "Board"
 * @param {Layout | null} existingLayout - Sets drawer in edit mode - The layout you are editing.
 */
const CreateTableLayoutDrawer = ({
  open,
  onClose,
  currentModule,
  handleCreate,
  handleUpdate,
  updateKey = "TableLayouts",
  title = "Table",
  submitting,
  existingLayout, // Used when Updating an existing layout.
  fieldSchema,
  limit,
  useModulePicker = false,
  useTypeSelect = false,
  usePageSelection = true,
  existingLayouts,
  handleAddGlobals,
  layoutT = "Table",
}: Props) => {
  usePageSelection = existingLayouts ? usePageSelection : false;
  const [page, setPage] = useState("selection");
  const [module, setModule] = useState<Module | null>(currentModule);
  const [layoutType, setLayoutType] = useState("");
  const [layoutLimit, setLayoutLimit] = useState(limit);
  let { getModule } = useModule();
  const [error, setError] = useState<DrawerError | null>(null);
  const [selectedLayouts, setSelectedLayouts] = useState({});
  const { workspaceID, tenantID } = useParams();
  const [orderVal, toggleOrder] = useToggle();

  // Core Layout State
  const [name, setName] = useState("");
  const [fields, setFields] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState("");
  const [timeFrame, setTimeFrame] = useState("");
  const [emptyGroup, setEmptyGroup] = useState(false);
  const [backFields, setBackFields] = useState<string[]>([]);
  const [cardType, setCardType] = useState<CardTypes>("Default");
  const [cardImage, setCardImage] = useState("");

  const {
    fieldGroups,
    groupsLoading,
    reorderGroups,
    deleteGroup,
    resetGroups,
  } = useCalculateFieldGroups(
    tenantID || null,
    workspaceID,
    groupBy || null,
    module?.APIName || null,
    emptyGroup,
    existingLayout?.GroupBy,
    // @ts-ignore
    existingLayout?.Order || undefined,
    module!
  );

  const finalPage = existingLayout
    ? "normal"
    : usePageSelection
    ? page
    : "normal";
  const isNormal = finalPage === "normal";
  const isSelection = finalPage === "selection";
  const isTimeline =
    updateKey === "TimelineLayouts" || layoutType === "Timeline";
  const isTable = updateKey === "TableLayouts" || layoutType === "Table";
  const usesGroupBy =
    GroupByLayouts.includes(updateKey) ||
    !!(existingLayout && GroupByTypes.includes(existingLayout?.Type!)) ||
    GroupByTypes.includes(layoutType);
  const usesEmptyGroup =
    GroupByLayouts.includes(updateKey) ||
    !!(existingLayout && GroupByTypes.includes(existingLayout?.Type!)) ||
    GroupByTypes.includes(layoutType);

  const availableFields = useMemo(() => {
    if (!module?.Fields?.length) return [];

    if (
      fieldSchema?.FieldType === "MultiSubitem" &&
      fieldSchema.SubitemFields
    ) {
      return fieldSchema.SubitemFields;
    }

    return module.Fields.flatMap((field) => {
      const { APIName, FieldType } = field;

      const fieldsToSkip = ["id", "_RecordName"];

      if (usesGroupBy) {
        fieldsToSkip.push("Subitem", "MultiSubitem");
      }

      // Fields to Skip
      if (fieldsToSkip.includes(APIName)) {
        return [];
      }

      // Manage Subitem Field + Nested Subitem Fields
      if (FieldType === "Subitem" && field.SubitemFields) {
        const allSubitemFields = field.SubitemFields.map((subField) => {
          return {
            ...subField,
            Name: `${field.Name} - ${subField.Name}`,
            APIName: `${field.APIName}.${subField.APIName}`,
          };
        });

        // Add the Subitem Field itself to front of array.
        allSubitemFields.unshift(field);
        return allSubitemFields;
      }

      return field;
    });
  }, [usesGroupBy, module?.Fields, fieldSchema]);

  useEffect(() => {
    setModule(currentModule);
    setLayoutLimit(limit);
  }, [open, currentModule?.APIName, limit]);

  // Sync existing layout with local state.
  useEffect(() => {
    if (!existingLayout) return;

    if (currentModule) setModule(currentModule);

    setName(existingLayout?.Name ?? "");
    setGroupBy(existingLayout?.GroupBy || "");
    setTimeFrame(existingLayout?.TimeFrame || "");
    setBackFields(existingLayout?.BackFields || []);
    setCardImage(existingLayout?.CardImage || "");
    setCardType(existingLayout?.CardType || "Default");
    setFields(
      existingLayout?.Fields?.filter(
        (f: any) => !["id", "_RecordName"].includes(f)
      ) ?? []
    );

    setEmptyGroup(existingLayout?.EmptyGroup ?? false);

    if (useTypeSelect) {
      setLayoutType(existingLayout?.Type);
      setLayoutLimit(existingLayout?.Type !== "Table" ? 6 : undefined);
    }

    if (useModulePicker) {
      const module = getModule(existingLayout?.ModuleAPIName);
      setModule(module!);
    }
  }, [existingLayout]);

  const onAddGlobals = () => {
    const ids = Object.keys(selectedLayouts);

    if (!ids.length) return;

    const globals = ids.map((id) => ({ id: StrUtils.uuidv4(), GlobalID: id }));

    const newLayouts = [...(existingLayouts || []), ...globals];

    handleAddGlobals(newLayouts, onSuccess);
  };

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      headerTitle={`${
        existingLayout ? "Update" : isNormal || isSelection ? "New" : "Create"
      } ${title} Layout${!isNormal ? "s" : ""}`}
      error={error}
      headerOptions={
        !isSelection ? (
          <Button
            size="sm"
            color="primary"
            onClick={
              existingLayout || finalPage === "normal"
                ? handleCreateUpdateLayout
                : onAddGlobals
            }
            disabled={
              finalPage === "normal"
                ? submitting ||
                  !fields?.length ||
                  !name ||
                  (useModulePicker && !module) ||
                  (useTypeSelect && !layoutType) ||
                  (usesGroupBy && !isTable && !groupBy) ||
                  (updateKey === "CardsLayouts" && isCardsDisabled())
                : !Object.keys(selectedLayouts).length
            }
            style={{ width: 100 }}
          >
            {submitting ? (
              <AppSpinner color="white" />
            ) : existingLayout ? (
              "Update"
            ) : finalPage === "normal" ? (
              "Create"
            ) : (
              "Add"
            )}
          </Button>
        ) : undefined
      }
      bodyCSS="pt-3 pb-8"
      body={(containerRef) => (
        <>
          {finalPage === "selection" && (
            <SelectionPage setPage={setPage} type="Layout" />
          )}
          {finalPage === "global" && (
            <CreateFromGlobalPage
              selectedGlobals={selectedLayouts}
              setSelectedGlobals={setSelectedLayouts}
              existingValues={existingLayouts || []}
              moduleAPIName={module?.APIName}
              globalType="Layouts"
              layoutType={layoutT}
            />
          )}
          {finalPage === "normal" && (
            <>
              <RequiredTitle title={`${title} Layout Name`} size="md" />
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={useTypeSelect ? "mb-3" : undefined}
              />

              {useTypeSelect && (
                <>
                  <RequiredTitle title={`${title} Layout Type`} size="md" />
                  <Input
                    type="select"
                    value={layoutType}
                    disabled={Boolean(existingLayout && layoutType)}
                    onChange={(e) => {
                      let value = e.target.value;
                      let limit = value !== "Table" ? 6 : undefined;

                      setLayoutType(value);
                      setLayoutLimit(limit);

                      if (limit !== undefined && fields?.length > limit) {
                        return setError({
                          message: `You can only add ${limit} fields.`,
                          onClear: () => setError(null),
                        });
                      }

                      setError(null);
                    }}
                  >
                    <option value="" disabled>
                      -- Select Type --
                    </option>
                    <option value="Table">Table</option>
                    <option value="Cards">Card</option>
                    <option value="Board">Kanban</option>
                    <option value="Timeline">Timeline</option>
                  </Input>
                </>
              )}

              <hr className="my-4" />
              {useModulePicker && (
                <>
                  <RequiredTitle title={`Select Module`} size="md" />
                  <ModulePicker
                    onChange={(modAPIName) => {
                      let module = getModule(modAPIName);
                      setModule(module!);
                    }}
                    value={module?.APIName}
                    styles={useModulePicker ? "mb-3" : undefined}
                  />
                </>
              )}
              {usesGroupBy &&
                layoutType !== undefined &&
                layoutType !== "Cards" &&
                module && (
                  <BoardGroupBy
                    mod={module}
                    groupBy={groupBy}
                    setGroupBy={setGroupBy}
                    timeFrame={timeFrame}
                    setTimeFrame={setTimeFrame}
                    isTimeline={isTimeline}
                    isTable={isTable}
                    emptyGroup={emptyGroup}
                    setEmptyGroup={setEmptyGroup}
                    containerRef={containerRef}
                  />
                )}
              {module && title !== "Cards" && (
                <>
                  <RequiredTitle title="Fields To Display" size="md" />
                  <ModuleFieldsPicker
                    module={module}
                    availableFields={availableFields}
                    values={fields}
                    setValues={setFields}
                    fieldSchema={fieldSchema}
                    layoutLimit={layoutLimit}
                    setError={setError}
                    onChange={moduleFieldsChange}
                  />
                </>
              )}
              <hr className="my-4" />
              {groupBy && !isTable && (
                <CustomToggle
                  title="Empty Values Group"
                  description="Your layout will show an additional group for records with empty value."
                  value={emptyGroup ?? false}
                  handleChange={(e) => setEmptyGroup(e.target.checked)}
                  containerCSS="my-4"
                />
              )}
              {groupBy && (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h4 className="mb-0">Customize the Group Order</h4>
                    <div className="d-flex align-items-center">
                      <div
                        onClick={orderAlphabetically}
                        className={`small 
                          ${
                            fieldGroups.length > 15
                              ? "cursor-disabled"
                              : "cursor-pointer"
                          }`}
                      >
                        {orderVal ? "A - Z" : "Z - A"}
                      </div>
                      <IconButton
                        className="ml-2"
                        icon="refresh"
                        onClick={resetGroups}
                        tooltipText="Reset Groups"
                      />
                    </div>
                  </div>
                  {groupsLoading && <SkeletonLoader />}
                  {!groupsLoading && (
                    <ManageItems
                      itemKey={"Name"}
                      idKey="Value"
                      items={
                        fieldGroups?.length > 15
                          ? ([] as FieldGroup[])
                          : fieldGroups
                      }
                      description={
                        !fieldGroups || fieldGroups.length === 0
                          ? "No groups available."
                          : fieldGroups.length > 15
                          ? "Reorder for large lists not supported."
                          : "Drag and drop to reorder groups."
                      }
                      title={"Groups"}
                      onReorder={reorderGroups}
                      onDelete={deleteGroup}
                    />
                  )}
                </>
              )}
              {module && title === "Cards" && (
                <CardsLayoutEditor
                  module={module}
                  availableFields={availableFields}
                  fieldSchema={fieldSchema}
                  layoutLimit={5}
                  setError={setError}
                  moduleFieldsChange={moduleFieldsChange}
                  frontFields={fields}
                  setFrontFields={setFields}
                  backFields={backFields}
                  setBackFields={setBackFields}
                  cardType={cardType}
                  setCardType={setCardType}
                  cardImage={cardImage}
                  setCardImage={setCardImage}
                />
              )}
            </>
          )}
        </>
      )}
    />
  );

  function orderAlphabetically() {
    let newOrder: string[] = [];

    if (orderVal) {
      toggleOrder();
      newOrder = fieldGroups
        ?.sort((a, b) => a.Name.localeCompare(b.Name))
        .map((group) => group.Value);
    } else {
      toggleOrder();
      newOrder = fieldGroups
        ?.sort((a, b) => b.Name.localeCompare(a.Name))
        .map((group) => group.Value);
    }

    reorderGroups({ newOrder });
  }

  function isCardsDisabled() {
    if (["Image", "Folder"].includes(cardType) && !cardImage) return true;

    return false;
  }

  function moduleFieldsChange(newFields: string[]) {
    if (layoutLimit && newFields?.length > layoutLimit) {
      // For Global Layout Create/Update
      // Giving to the user the ability to unselect fields when swichs from
      // other layout types which limited fields and has selected more than six fields.
      if (useTypeSelect && newFields?.length < fields?.length)
        setFields(newFields);

      return setError({
        message: `You can only add ${layoutLimit} fields.`,
        onClear: () => setError(null),
      });
    }

    setError(null);
    setFields(newFields);
  }

  function reset() {
    setName("");
    setFields([]);
    setError(null);
    setLayoutType("");
    setGroupBy("");
    setTimeFrame("");
    setBackFields([]);
    setCardType("Default");
    setCardImage("");
  }

  function handleClose() {
    setPage("selection");
    setSelectedLayouts({});
    setEmptyGroup(false);
    reset();
    onClose();
    setModule(null);
  }

  function onSuccess() {
    handleClose();
  }

  function handleCreateUpdateLayout() {
    const payload: Record<string, any> = {
      Name: name,
      Fields: fieldSchema ? fields : ["id", "_RecordName"].concat(fields),
      ...(useTypeSelect && { Type: layoutType }),
      ...(useModulePicker && { ModuleAPIName: module?.APIName }),
      ...(usesGroupBy && {
        GroupBy: groupBy,
      }),
      ...(usesEmptyGroup && { EmptyGroup: emptyGroup }),
      ...(isTimeline && { TimeFrame: timeFrame }),
    };

    if (updateKey === "CardsLayouts") {
      payload.CardType = cardType;

      if (backFields?.length) payload.BackFields = backFields;
      if (["Image", "Folder"].includes(cardType) && cardImage)
        payload.CardImage = cardImage;
    }

    if (updateKey === "BoardLayouts" || updateKey === "TableLayouts") {
      payload.Order = fieldGroups?.map((group) => group.Value);
    }

    if (existingLayout && handleUpdate) {
      handleUpdate({
        key: updateKey as any,
        id: existingLayout.id,
        payload: payload,
        onSuccess: onSuccess,
      });
    } else {
      handleCreate({
        key: updateKey.substring(0, updateKey.length - 1) as any,
        payload: payload,
        onSuccess: onSuccess,
      });
    }
  }
};

const BoardGroupByData = {
  Board: {
    Title: "Group By",
    Description:
      "Your data will be grouped in columns regarding your selection.",
  },
  Timeline: {
    Title: "Group By Field",
    Description: "The date/time field to compare against.",
  },
  Table: {
    Title: "Group By Field (optional)",
    Description: "Grouped your data separate table sections based on a field.",
  },
};

const TimeRanges = [
  { label: "Day", value: "day" },
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
  { label: "Quarter", value: "quarter" },
  { label: "Year", value: "year" },
];

interface BoardGroupByProps {
  mod: Module;
  groupBy: string;
  setGroupBy: React.Dispatch<React.SetStateAction<string>>;
  timeFrame: string;
  setTimeFrame: React.Dispatch<React.SetStateAction<string>>;
  isTimeline: boolean;
  isTable: boolean;
  emptyGroup: boolean;
  setEmptyGroup: React.Dispatch<React.SetStateAction<boolean>>;
  containerRef?: any;
}

function BoardGroupBy({
  mod,
  groupBy,
  setGroupBy,
  timeFrame,
  setTimeFrame,
  isTimeline,
  isTable,
  containerRef,
}: BoardGroupByProps) {
  // Fields are different for Timeline vs Board
  const selectableFields = useMemo(() => {
    if (isTimeline) {
      return (
        mod?.Fields?.filter((f) =>
          ["Date", "DateTime"].includes(f?.FieldType)
        ) ?? []
      ).map((f) => ({ label: f?.Name, value: f?.APIName }));
    }

    let validFields: Field[] = [];

    if (isTable) {
      validFields =
        mod?.Fields?.filter((f) =>
          [
            "SingleSelect",
            "MultiSelect",
            "SingleReference",
            "MultiReference",
            "Tags",
            "Text",
            "Number",
            "Percent",
            "Currency",
            "CurrencyInterval",
            "Calculated",
            "Location",
          ].includes(f?.FieldType)
        ) ?? [];
    } else {
      validFields =
        mod?.Fields?.filter((f) =>
          [
            "SingleSelect",
            "SingleReference",
            "MultiReference",
            "Tags",
            "Text",
            "Number",
            "Percent",
            "Location",
          ].includes(f?.FieldType)
        ) ?? [];
    }

    const options: { label: string; value: string }[] = validFields.flatMap(
      (field) => {
        if (field?.FieldType === "Location") {
          return [
            {
              label: `${field.Name} - City`,
              value: `${field.APIName}.Address.City`,
            },
            {
              label: `${field.Name} - State`,
              value: `${field.APIName}.Address.StateProvince`,
            },
            {
              label: `${field.Name} - Country`,
              value: `${field.APIName}.Address.Country`,
            },
          ];
        }

        return [
          {
            label: field.Name as string,
            value: field.APIName as string,
          },
        ];
      }
    );

    return options;
  }, [mod?.Fields, isTimeline, isTable]);

  const data =
    BoardGroupByData[isTimeline ? "Timeline" : isTable ? "Table" : "Board"];
  return (
    <>
      {isTable ? (
        <h4 className="mb-0">{data.Title}</h4>
      ) : (
        <RequiredTitle title={data.Title} size="md" titleCSS="mb-0" />
      )}
      <div className="mb-1 small">{data.Description}</div>
      <SelectPicker
        data={selectableFields}
        block
        placeholder="Select Group By Field"
        className="mb-4"
        onChange={(val) => setGroupBy(val || "")}
        value={groupBy}
        onClean={() => setGroupBy("")}
        container={containerRef ? () => containerRef.current! : undefined}
      />
      {isTimeline && (
        <>
          <RequiredTitle
            title="Group By Time Range"
            size="md"
            titleCSS="mb-0"
          />
          <div className="mb-1 small">
            Your data will be grouped in columns regarding your selection.
          </div>
          <SelectPicker
            data={TimeRanges}
            block
            placeholder="Select Time Range"
            className="mb-4"
            onChange={(val) => setTimeFrame(val || "")}
            value={timeFrame}
          />
        </>
      )}
    </>
  );
}

export default CreateTableLayoutDrawer;
